import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../api/client";
import { theme, emptyFrame, type as text } from "../../theme";
import { DimensionHeading, IndexTabs, SheetCard } from "../../components/Drawing";
import { RegisterToolbar } from "../../components/RegisterToolbar";
import { LoadingBlock, ErrorBlock } from "../../components/Spinner";
import { by, downloadCsv, matchesSearch, type ExportColumn, type SortOption } from "../../lib/registerExport";
import { fmtDate, fmtDateTime } from "../../lib/format";
import { fmtDateOnly } from "../../lib/dateOnly";
import { frameworkLabel } from "../../nav/frameworkMeta";

// ── Audit tab: Registers ──────────────────────────────────────────────────
// Read-only views of the client's live registers (GET /auditor/clients/:orgId/registers).
// The API decides which registers this auditor may read (the live framework scope applied to
// backend/src/lib/auditorRegisters.ts) and which columns each carries; this page only lays out
// what it is sent, so a column never shown to an auditor is never in the browser either.

type ColumnType = "text" | "longtext" | "date" | "instant" | "datetime" | "number" | "flag" | "ref" | "status";
type Cell = string | number | boolean | null;
type Row = Record<string, Cell>;

interface RegisterColumn { key: string; label: string; type: ColumnType }

interface RegisterSummary {
  kind: string;
  label: string;
  description: string;
  frameworkIds: string[];
  sections: Array<{ key: string; label: string }>;
}

interface RegisterSection {
  key: string;
  label: string;
  columns: RegisterColumn[];
  rows: Row[];
  truncated: boolean;
}

interface RegisterView {
  kind: string;
  label: string;
  description: string;
  frameworkIds: string[];
  sections: RegisterSection[];
}

/** snake_case or camel values as words: under_treatment -> Under treatment. */
function humanise(v: string): string {
  const words = v.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The value as text, the way the cell shows it and the CSV carries it. */
function display(value: Cell, type: ColumnType): string {
  if (value === null || value === "") return "";
  switch (type) {
    case "date":     return fmtDateOnly(String(value));
    case "instant":  return fmtDate(String(value));
    case "datetime": return fmtDateTime(String(value));
    case "flag":     return value ? "Yes" : "No";
    case "status":   return humanise(String(value));
    default:         return String(value);
  }
}

const th: CSSProperties = {
  padding: "8px 10px", borderBottom: "1px solid var(--c-line)", textAlign: "left", whiteSpace: "nowrap",
  ...text.label, margin: 0, display: "table-cell",
};
const td: CSSProperties = { padding: "8px 10px", verticalAlign: "top", borderBottom: "1px solid var(--c-border)" };
const chip: CSSProperties = {
  display: "inline-block", padding: "1px 7px", borderRadius: 2, fontSize: 11, fontWeight: 600, lineHeight: "17px",
  border: "1px solid var(--c-border)", color: "var(--c-text)", background: "var(--c-panel)", whiteSpace: "nowrap",
};

function CellView({ value, type }: { value: Cell; type: ColumnType }) {
  if (value === null || value === "") return <span style={{ color: theme.color.faint }}>—</span>;
  const shown = display(value, type);
  if (type === "status") return <span style={chip}>{shown}</span>;
  if (type === "ref") return <span style={{ ...text.ref, color: "var(--c-ref)", whiteSpace: "nowrap" }}>{shown}</span>;
  if (type === "number") return <span style={{ fontVariantNumeric: "tabular-nums" }}>{shown}</span>;
  if (type === "longtext") {
    return (
      <span title={shown} style={{
        display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden",
        whiteSpace: "pre-wrap", minWidth: 200, maxWidth: 360,
      }}>{shown}</span>
    );
  }
  if (type === "date" || type === "instant" || type === "datetime") return <span style={{ whiteSpace: "nowrap" }}>{shown}</span>;
  return <span>{shown}</span>;
}

function sortOptionsFor(columns: RegisterColumn[]): SortOption<Row>[] {
  return columns
    .filter((c) => c.type !== "longtext" && c.type !== "flag")
    .map((c) => {
      const get = (r: Row) => r[c.key];
      const compare =
        c.type === "number" ? by.number<Row>((r) => (typeof get(r) === "number" ? get(r) as number : null))
        : c.type === "date" || c.type === "instant" || c.type === "datetime" ? by.date<Row>((r) => (get(r) as string | null), "desc")
        : by.text<Row>((r) => (get(r) == null ? null : String(get(r))));
      return { key: c.key, label: c.label, compare };
    });
}

function SectionSheet({ section, registerLabel, clientRef }: { section: RegisterSection; registerLabel: string; clientRef: string }) {
  const [search, setSearch] = useState("");
  const sortOptions = useMemo(() => sortOptionsFor(section.columns), [section.columns]);
  const [sort, setSort] = useState(sortOptions[0]?.key ?? "");

  const rows = useMemo(() => {
    const filtered = section.rows.filter((r) => matchesSearch(search, ...section.columns.map((c) => display(r[c.key], c.type))));
    const option = sortOptions.find((o) => o.key === sort);
    return option ? [...filtered].sort(option.compare) : filtered;
  }, [section, search, sort, sortOptions]);

  const exportCsv = () => {
    const columns: ExportColumn<Row>[] = section.columns.map((c) => ({ header: c.label, value: (r) => display(r[c.key], c.type) }));
    const name = `${clientRef}-${registerLabel}-${section.label}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    downloadCsv(name, columns, rows);
  };

  return (
    <SheetCard label={section.label} reference={`${section.rows.length} ${section.rows.length === 1 ? "entry" : "entries"}`}>
      {section.rows.length === 0 ? (
        <div style={{ ...emptyFrame, color: theme.color.muted, padding: 24, fontSize: 13 }}>Nothing recorded yet.</div>
      ) : (
        <>
          <RegisterToolbar<Row>
            search={search}
            onSearch={setSearch}
            placeholder={`Search ${section.label.toLowerCase()}`}
            sortOptions={sortOptions}
            sort={sort}
            onSort={setSort}
            onExport={rows.length > 0 ? exportCsv : undefined}
            count={{ shown: rows.length, total: section.rows.length }}
          />
          <div style={{ overflowX: "auto" }}>
            <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{section.columns.map((c) => <th key={c.key} scope="col" style={th}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={section.columns.length} style={{ ...td, color: theme.color.muted }}>No {section.label.toLowerCase()} match your search.</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={i}>
                    {section.columns.map((c) => <td key={c.key} style={td}><CellView value={r[c.key]} type={c.type} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {section.truncated && (
            <div style={{ ...text.hint, marginTop: 8 }}>
              Only the first {section.rows.length} entries are shown. Ask the client for the full register as an export if you need every row.
            </div>
          )}
        </>
      )}
    </SheetCard>
  );
}

export function AuditorRegistersTab() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  // The client's name for export file names (the audit layout's outlet carries its detail).
  const outlet = useOutletContext<{ detail?: { name?: string } } | undefined>();
  const clientRef = outlet?.detail?.name || "client";
  const [params, setParams] = useSearchParams();
  const [registers, setRegisters] = useState<RegisterSummary[] | null>(null);
  const [listError, setListError] = useState("");
  const [view, setView] = useState<RegisterView | null>(null);
  const [viewError, setViewError] = useState("");

  const loadList = useCallback(() => {
    setListError("");
    return api.get<{ registers: RegisterSummary[] }>(`/auditor/clients/${orgId}/registers`)
      .then((d) => setRegisters(d.registers))
      .catch(() => setListError("Failed to load the registers."));
  }, [orgId]);
  useEffect(() => { loadList(); }, [loadList]);

  const requested = params.get("register");
  const selected = registers?.find((r) => r.kind === requested)?.kind ?? registers?.[0]?.kind ?? null;

  const loadView = useCallback(() => {
    if (!selected) return;
    setView(null);
    setViewError("");
    api.get<RegisterView>(`/auditor/clients/${orgId}/registers/${encodeURIComponent(selected)}`)
      .then(setView)
      .catch((e) => setViewError(e instanceof ApiError && e.status === 404
        ? "This register is not part of your audit scope for this organisation."
        : "Failed to load the register."));
  }, [orgId, selected]);
  useEffect(() => { loadView(); }, [loadView]);

  if (listError) return <ErrorBlock message={listError} onRetry={loadList} />;
  if (!registers) return <LoadingBlock />;

  if (registers.length === 0) {
    return (
      <div style={{ ...emptyFrame, color: theme.color.muted, padding: 32, fontSize: 13 }}>
        None of the standards in your audit scope for this organisation draws on its registers.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <IndexTabs
        label="Registers"
        items={registers.map((r) => ({ id: r.kind, label: r.label }))}
        value={selected ?? registers[0].kind}
        onChange={(kind) => setParams((p) => { const next = new URLSearchParams(p); next.set("register", kind); return next; }, { replace: true })}
        style={{ marginBottom: 0 }}
      />
      {viewError && <ErrorBlock message={viewError} onRetry={loadView} />}
      {!viewError && !view && <LoadingBlock />}
      {view && (
        <>
          <div>
            <DimensionHeading count={view.frameworkIds.map(frameworkLabel).join(" · ")}>{view.label}</DimensionHeading>
            <div style={{ ...text.hint }}>{view.description} Shown because it is evidence for the standards named above.</div>
          </div>
          {view.sections.map((s) => (
            <SectionSheet key={`${view.kind}:${s.key}`} section={s} registerLabel={view.label} clientRef={clientRef} />
          ))}
        </>
      )}
      <div style={{ fontSize: 11, color: "var(--c-subtle)", fontStyle: "italic", paddingBottom: 8 }}>
        Read-only view of the client's live registers, limited to the standards your bureau is engaged for. Commercial
        figures, internal notes and contact details are never shown. Raise a finding against a control to act on an entry.
      </div>
    </div>
  );
}
